import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { CheckCircle, X, RotateCcw } from 'lucide-react';

const ContractSignature = () => {
  const { verificationCode } = useParams();
  const [contract, setContract] = useState(null);
  const [signature, setSignature] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (verificationCode) {
      fetchContractByCode(verificationCode);
    }
  }, [verificationCode]);

  const fetchContractByCode = async (code) => {
    try {
      // This would be a new endpoint to fetch contract by verification code
      const response = await axios.get(`/api/properties/contracts/verify/${code}`);
      setContract(response.data.data);
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast.error('Invalid or expired signature link');
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    setSignature(canvas.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature('');
  };

  const handleSubmitSignature = async () => {
    if (!signature) {
      toast.error('Please provide your signature');
      return;
    }

    setIsSigning(true);
    try {
      const response = await axios.put(`/api/properties/contracts/signatures/${contract.signatureId}`, {
        signature_data: signature,
        signed_at: new Date().toISOString(),
        ip_address: await getClientIP(),
        user_agent: navigator.userAgent,
        is_verified: true
      });

      if (response.data.success) {
        toast.success('Contract signed successfully!');
        setContract({ ...contract, esign_status: 'signed' });
      }
    } catch (error) {
      console.error('Error submitting signature:', error);
      toast.error('Failed to submit signature');
    } finally {
      setIsSigning(false);
    }
  };

  const getClientIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <X className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Invalid Signature Link</h3>
          <p className="mt-1 text-sm text-gray-500">This signature link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Contract Signature</h1>
              <p className="mt-2 text-gray-600">Please review and sign the contract below</p>
            </div>

            {/* Contract Details */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contract Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Property</label>
                  <p className="mt-1 text-sm text-gray-900">{contract.property_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tenant</label>
                  <p className="mt-1 text-sm text-gray-900">{contract.tenant_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Monthly Rent</label>
                  <p className="mt-1 text-sm text-gray-900">${contract.monthly_rent}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Lease Period</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {contract.lease_start} to {contract.lease_end}
                  </p>
                </div>
              </div>
            </div>

            {/* Contract Content */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contract Terms</h2>
              <div className="prose max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                  {contract.contract_content || 'Contract content will be displayed here...'}
                </pre>
              </div>
            </div>

            {/* Signature Section */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Digital Signature</h2>
              <p className="text-sm text-gray-600 mb-4">
                Please sign in the box below using your mouse or touchpad.
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="border border-gray-300 rounded cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const mouseEvent = new MouseEvent('mousedown', {
                      clientX: touch.clientX,
                      clientY: touch.clientY
                    });
                    canvasRef.current.dispatchEvent(mouseEvent);
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const mouseEvent = new MouseEvent('mousemove', {
                      clientX: touch.clientX,
                      clientY: touch.clientY
                    });
                    canvasRef.current.dispatchEvent(mouseEvent);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    const mouseEvent = new MouseEvent('mouseup', {});
                    canvasRef.current.dispatchEvent(mouseEvent);
                  }}
                />
              </div>
              
              <div className="mt-4 flex justify-between">
                <button
                  onClick={clearSignature}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Clear
                </button>
                <p className="text-sm text-gray-500 self-center">
                  By signing, you agree to the terms of this contract
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              {contract.esign_status === 'signed' ? (
                <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Contract Signed Successfully
                </div>
              ) : (
                <button
                  onClick={handleSubmitSignature}
                  disabled={!signature || isSigning}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSigning ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Signing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Sign Contract
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Legal Notice */}
            <div className="mt-8 text-center">
              <p className="text-xs text-gray-500">
                This electronic signature has the same legal effect as a handwritten signature.
                By signing this document, you acknowledge that you have read, understood, and agree to be bound by the terms and conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractSignature;
